import { isAxiosError } from 'axios';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToastStore } from '@/stores/toast';
import { handleAxiosError } from '@/utils/handleAxiosError';

type CallbackFn = (newWindow: Window) => Promise<void> | void;
type ErrorCallbackFn = (err: unknown) => void;
/**
 * Custom React hook to handle actions that require opening a new browser window (`_blank`)
 * with loading state and error handling.
 * Useful when you need to perform an async operation (e.g., creating a chat or appointment),
 * then redirect the new window to a resulting URL.
 * Automatically handles:
 * - Blocked popups (shows toast)
 * - Loading state
 * - Axios errors (with centralized `handleAxiosError`)
 * - Optional custom error handling via `errorCallback`
 * @returns An object containing:
 *  - `runWithWindow`: Function to run a callback with the new window. Accepts an optional error callback.
 *  - `loading`: Boolean indicating whether the action is in progress.
 * @example
 * const { runWithWindow, loading } = useWindowBlankAction();
 * const handleClick = () => {
 *   runWithWindow(async (window) => {
 *     const result = await createSomething();
 *     win.location.href = `/some-path/${result.id}`;
 *   });
 * };
 */
export const useWindowBlankAction = () => {
  const [loading, setLoading] = useState(false);
  const setToast = useToastStore((state) => state.setToast);
  const { t } = useTranslation();

  const runWithWindow = useCallback(
    async (callback: CallbackFn, errorCallback?: ErrorCallbackFn) => {
      const newWindow = window.open('', '_blank');

      if (!newWindow) {
        setToast({
          title: t('pop-ups-are-blocked'),
          description: t('enable-them-in-your-browser-settings'),
          variant: 'error',
        });
        return;
      }

      setLoading(true);

      try {
        await callback(newWindow);
      } catch (err) {
        console.error('Window callback failed:', err);
        newWindow?.close();
        if (errorCallback) {
          errorCallback(err);
          return;
        }
        if (isAxiosError(err)) {
          handleAxiosError(err, setToast);
        }
      } finally {
        setLoading(false);
      }
    },
    [setToast, t],
  );

  return { runWithWindow, loading };
};
